<template>
  <div class="windowWrapper">

    <div
        class="window"
        :class="{hideWindow: hide, breaking: type === 'breaking', wiim: type === 'wiim'}"
        :style="windowStyle()"
        :theme="systemTheme"
        @mousedown="bringToFront"
        ref="windowRef">

      <div class="symbolSearchWrapper" v-if="searchInputVisible">

        <div class="symbolSearchContent">
          <symbols-search :with-preview="true" :as-page="true" :overflow="true" :big="true" :add-icon="true"
                          @close="searchInputVisible = false"
                          :excludeFromAdd="tabSymbols ?? []"
                          @addIcon="$emit('addWSymbolToTabs', $event)"/>
        </div>
      </div>

      <div class="header" :theme="systemTheme" @mousedown.prevent="startDrag">
        <slot name="header">
          <div class="headerTools" :theme="systemTheme">
            <i class="ri-close-line" @click="$emit('close')"/>
            <span id="title" v-if="title">{{ title }}</span>
            <!--          <i class="ri-subtract-line" @click="$emit('minimize')"/>-->
            <i :style="setSpacing()" class="ri-draggable"/>
          </div>
        </slot>
      </div>

      <div class="body" @scroll="handleScroll" ref="body">
        <slot :showSymbolSearchInput="showSymbolSearchInput"/>
      </div>

      <div class="resizer-handle" @mousedown.prevent="startResize">
        <i class="ri-expand-diagonal-2-line"/>
      </div>
    </div>

  </div>

</template>

<script>
import SearchBar from "./SearchBar";
import SymbolsSearch from "./SymbolsSearch";

export default {
  name: 'Window',
  components: {SymbolsSearch, SearchBar},
  props: ['hide', 'type', 'title', 'tabSymbols', 'refName', 'bodyRef'],
  data() {
    return {
      searchInputVisible: false,
      position: {x: 100, y: 100},
      size: {width: 400, height: 300},
      isDragging: false,
      isResizing: false,
      start: {x: 0, y: 0, width: 0, height: 0},
      zIndex: 12,
    }
  },
  mounted() {
    const screenWidth = window.innerWidth
    const screenHeight = window.innerHeight

    const numberOfOpenedWindows = document.querySelectorAll('.windowWrapper').length - 1;

    this.size.width = screenWidth * 0.5;
    this.size.height = screenHeight * 0.9;

    this.position.x = ((screenWidth - this.size.width) / 2) + numberOfOpenedWindows * 15
    this.position.y = ((screenHeight - this.size.height) / 2) + numberOfOpenedWindows * 15
    this.bringToFront();

    this.$nextTick(() => {
      this.$emit('getRef', this.refName);
    });

  },
  methods: {
    handleScroll() {

      if (this.bodyRef?.loadNext) {
        const body = this.$refs.body;
        if (body.scrollTop + body.clientHeight >= body.scrollHeight * 0.7) {
          this.bodyRef?.loadNext();
        }
      }
    },
    windowStyle() {

      const isMobile = window.innerWidth <= 768;
      if (isMobile) {
        return {
          position: 'fixed',
          top: '0',
          left: '0',
          width: 'calc(100vw - 2px)',
          height: 'calc(100vh - 2px)',
          borderRadius: '0',
          zIndex: this.bringToFront(),
        }
      }

      return {
        position: 'absolute',
        left: this.position.x + 'px',
        top: this.position.y + 'px',
        width: this.size.width + 'px',
        height: this.size.height + 'px',
        zIndex: this.bringToFront(),
      }
    },
    showSymbolSearchInput() {
      this.searchInputVisible = true;
    },
    setSpacing() {

      const marginSide = this.uiDirection === 'rtl' ? 'marginRight' : 'marginLeft';
      return {[marginSide]: 'auto'}
    },
    bringToFront() {

      return Math.ceil(new Date().getTime() / 1000);
    },
    startDrag(e) {
      this.isDragging = true
      this.start.x = e.clientX - this.position.x
      this.start.y = e.clientY - this.position.y

      window.addEventListener('mousemove', this.onDrag)
      window.addEventListener('mouseup', this.stopDrag)
    },
    onDrag(e) {
      if (!this.isDragging) return

      const newX = e.clientX - this.start.x
      const newY = e.clientY - this.start.y

      // Prevent dragging the header above the viewport
      this.position.x = Math.max(0, newX)
      this.position.y = Math.max(0, newY)
    },
    stopDrag() {
      this.isDragging = false
      window.removeEventListener('mousemove', this.onDrag)
      window.removeEventListener('mouseup', this.stopDrag)
    },
    startResize(e) {
      this.isResizing = true
      this.start.x = e.clientX
      this.start.y = e.clientY
      this.start.width = this.size.width
      this.start.height = this.size.height

      window.addEventListener('mousemove', this.onResize)
      window.addEventListener('mouseup', this.stopResize)
    },
    onResize(e) {
      if (!this.isResizing) return
      const dx = e.clientX - this.start.x
      const dy = e.clientY - this.start.y
      this.size.width = Math.max(200, this.start.width + dx)
      this.size.height = Math.max(150, this.start.height + dy)
    },
    stopResize() {
      this.isResizing = false
      window.removeEventListener('mousemove', this.onResize)
      window.removeEventListener('mouseup', this.stopResize)
    },
  },
}
</script>

<style scoped lang="scss">

.windowWrapper {
  @media(max-width: 768px) {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.8);
    z-index: 99999;
  }
}

.window {
  border: 1px solid #aaa;
  background: var(--bgColor);
  overflow: hidden;
  border-radius: 6px;
  box-shadow: 0 5px 35px 5px rgba(0, 0, 0, 1);

  &[theme="lightMode"] {

    box-shadow: 0 5px 35px 5px rgba(0, 0, 0, .35);
  }

  &.breaking {
    border: 1px solid var(--red-dark);
  }

  &.wiim {
    border: 1px solid var(--purple);
  }
}

.header {
  background: var(--tableHoverColor);
  padding: 0.5em;
  cursor: move;
  font-weight: bold;

  &[theme="lightMode"] {
    background: var(--notficationBorderColor);

    .headerTools {
      color: var(--textColor);
    }

    #title {
      color: var(--bgColor);
    }
  }
}

.breaking {
  .header {
    background: var(--red-dark);
  }
}

.wiim {
  .header {
    background: var(--purple);
  }
}

.body {
  overflow-y: auto;
  overflow-x: hidden;
  height: calc(100% - 40px);
  @media(max-width: 768px) {
    height: calc(100% - 30px);
  }
}

.resizer-handle {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 24px;
  height: 24px;
  background: rgba(255, 255, 255, .55);
  border-top-left-radius: 8px;
  cursor: nwse-resize;
  display: flex;
  align-items: center;
  justify-content: center;

  i {
    font-size: 22px;
    color: black;
  }
}

.headerTools {
  display: flex;
  flex-direction: row;
  gap: 5px;

  &[theme="lightMode"] {
    color: var(--bgColor);
  }

  i {
    padding: 8px;
    margin: -8px;
    background: var(--tableHoverColorOpacity);
  }
}

.hideWindow {
  position: fixed;
  width: 0;
  height: 0;
  top: 0;
  left: 0;
  visibility: hidden;
}

#title {
  padding: 0 1rem;
  height: 18px;
  line-height: 14px;
}

.ri-close-line {
  cursor: pointer;
}

.ri-draggable,
.resizer-handle {
  @media(max-width: 768px) {
    display: none;
  }
}

.symbolSearchWrapper {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(20px);
  z-index: 999;

  .symbolSearchContent {
    padding: 1rem;
    box-sizing: border-box;
    position: relative;
  }
}

</style>
